import {
    FC,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react';

import { getRandomExampleOptions, searchSkillOptionsOnQuery, sortSkillOptions } from '../../../helpers/skills';
import trans from '../../../helpers/trans';
import { SkillBase } from '../../../models/@competentNL/Skills/Skills';
import { Loading, SearchInput, SkillList } from '../..';
import { SkillResults } from './subcomponents';

import './SkillsSelector.scss';

interface SkillsSelectorProps {
    isLoading?: boolean;
    showSearch?: boolean;
    value: SkillBase[];
    options?: SkillBase[];
    searchedOptions?: SkillBase[];
    error?: string;
    validationError?: string;
    onSearchChange?: (query: string) => void;
    onChange: (skills: SkillBase[]) => void;
    className?: string;
}

const SkillsSelector: FC<SkillsSelectorProps> = ({
    isLoading,
    showSearch,
    value,
    options = [],
    searchedOptions = [],
    error = '',
    validationError = '',
    onSearchChange,
    onChange,
    className = '',
}): ReactElement => {
    const defaultResults: SkillBase[] = [];

    const [placeholderExample, setPlaceholderExample] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [hasNewSearchQuery, setHasNewSearchQuery] = useState<boolean>(false);
    const [searchResults, setSearchResults] = useState<SkillBase[]>(defaultResults);

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect((): void => {
        if (options.length > 0 && !placeholderExample) {
            const example = getRandomExampleOptions(options);
            setPlaceholderExample(trans('common.example', { example }));
        }
    }, [options]);

    useEffect((): void => {
        setHasNewSearchQuery(false);
    }, [searchedOptions]);

    const handleSearch = (query: string): void => {
        setSearchQuery(query);
        setHasNewSearchQuery(true);

        if (onSearchChange) {
            onSearchChange(query);
        }

        if (query === '') {
            setSearchResults(defaultResults);
        } else {
            const results = searchSkillOptionsOnQuery(options, query);
            setSearchResults(results);
        }
    };

    const handleSelectOption = (selectedOption: SkillBase): void => {
        const optionExists = value.find(option => option.id === selectedOption.id);

        const updatedSelectedOptions = optionExists
            ? value.filter(option => option.id !== selectedOption.id)
            : [...value, selectedOption];

        const sortedSelectedOptions = sortSkillOptions(updatedSelectedOptions);

        onChange(sortedSelectedOptions);
    };

    const handleClearSearch = (): void => {
        if (inputRef.current) {
            inputRef.current.focus();
            setSearchQuery('');
            setSearchResults(defaultResults);
        }
    };

    return (
        <div className={`skills-selector ${className}`}>
            {showSearch && (
                <SearchInput
                    ref={inputRef}
                    label={trans('compositions.skillsSelector.searchLabel')}
                    value={searchQuery}
                    placeholder={placeholderExample}
                    disabled={!!error}
                    error={error || validationError}
                    onChange={handleSearch}
                />
            )}

            <SkillResults
                searchResults={searchResults}
                selectedOptions={value}
                onSelectOption={handleSelectOption}
                onClearSearch={handleClearSearch}
            />

            {isLoading && (
                <Loading className="skills-selector__loader" />
            )}

            {!showSearch && (
                <SkillList
                    title={trans('compositions.skillsSelector.occupationSkills')}
                    skills={options}
                    selectedSkills={value}
                    onSelectOption={handleSelectOption}
                    className="skills-selector__occupation-skills"
                />
            )}

            {searchQuery && !hasNewSearchQuery && searchedOptions.length > 0 && (
                <SkillList
                    title={trans('compositions.skillsSelector.searchedSkills')}
                    skills={searchedOptions}
                    selectedSkills={value}
                    onSelectOption={handleSelectOption}
                    className="skills-selector__occupation-skills"
                />
            )}

            {showSearch && value.length > 0 && (
                <SkillList
                    title={trans('compositions.skillsSelector.selected')}
                    skills={value}
                    selectedSkills={value}
                    onSelectOption={handleSelectOption}
                    className="skills-selector__selected-skills"
                />
            )}
        </div>
    );
};

export default SkillsSelector;
